import { useEffect, useRef, useState } from 'react';
import { mountGoogleButton } from '../lib/googleSignIn';

/**
 * Google's own "Sign in with Google" button.
 *
 * Google renders it into the element below and owns the click: it opens its own account chooser
 * and hands the credential back through the callback. We do not style it and we do not intercept
 * the press — see `lib/googleSignIn.ts` for why a styled button of ours calling
 * `google.accounts.id.prompt()` pressed to no effect, which is the defect this replaces.
 *
 * `error` is the other half of that fix. The old path could fail with nothing on screen; here
 * every failure to load, initialise or render says so, and the Microsoft sign-in link beside it
 * stays usable either way.
 */
export function GoogleSignInButton({ onToken }: { onToken: (token: string) => void }) {
  const host = useRef<HTMLDivElement | null>(null);
  const [error, setError] = useState<string | null>(null);

  // The callback is read through a ref so a new `onToken` on re-render does not re-mount Google's
  // button — re-rendering it would flash the button and drop a chooser the person already opened.
  const latest = useRef(onToken);
  latest.current = onToken;

  useEffect(() => {
    const el = host.current;
    if (!el) return;
    let cancelled = false;

    // Google's `width` is pixels and it refuses a percentage, so it is measured from the slot it
    // is going into. Clamped to what Google accepts (it caps at 400), with a floor so a container
    // that has not been laid out yet cannot produce a 0-wide button.
    const measured = Math.round(el.getBoundingClientRect().width);
    const width = Math.min(400, Math.max(220, measured || 320));

    void mountGoogleButton(
      el,
      { type: 'standard', theme: 'outline', size: 'large', text: 'signin_with', shape: 'rectangular', width },
      (token) => latest.current(token),
    ).catch((err: unknown) => {
      if (cancelled) return;
      setError(err instanceof Error ? err.message : 'Google sign-in could not start.');
    });

    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="space-y-2">
      {/* min-height reserves the button's own height, so the card does not jump as Google draws it. */}
      <div ref={host} className="flex min-h-[2.5rem] w-full justify-center" />
      {error && <p className="text-sm text-red-600 dark:text-error-dark">{error}</p>}
    </div>
  );
}
